import {
  AlarmClock,
  CheckCircle2,
  FileQuestion,
  type FileWarning,
  Hash,
  Hourglass,
  PenLine,
  Wallet,
} from "lucide-react"
import type { ReactNode } from "react"

import type { EstadoOrden } from "@/componentes/ordenes/tipos"
import { TarjetaKpi } from "@/componentes/ui/TarjetaKpi"

export type Alerta = "vencidas" | "por_vencer" | "sin_factura" | "sin_numero"

type MontoDivisa = { divisa: string; monto: string }

export type Indicadores = {
  por_estado: Record<EstadoOrden, { cantidad: number; montos: MontoDivisa[] }>
  alertas: Record<Alerta, number>
  pagado_mes: { divisa: string; cantidad: number; monto: string }[]
  mes: string
  proveedores: { proveedor_id: number; proveedor: string; cantidad: number }[]
  dias_por_vencer: number
}

/**
 * Los tramos del circuito, en su orden, con el color de su insignia.
 *
 * ⚠ **El color dice EN QUÉ PUNTO está, no «bien» o «mal»**, y son los mismos tonos que
 * `TONO_ESTADO`: la barra, la tarjeta y la insignia de la fila hablan el mismo idioma.
 * Nunca va solo: cada tramo lleva su nombre y su cifra en la leyenda.
 */
const TRAMOS: { estado: EstadoOrden; etiqueta: string; color: string }[] = [
  { estado: "BORRADOR", etiqueta: "Borrador", color: "var(--fg-muted)" },
  { estado: "EN_APROBACION", etiqueta: "En aprobación", color: "var(--warning)" },
  { estado: "APROBADA", etiqueta: "Por pagar", color: "var(--info)" },
  { estado: "EJECUTADA", etiqueta: "Pagada", color: "var(--success)" },
]

const MESES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
]

/**
 * El tablero de la bandeja: tarjetas, el circuito y lo que se está atascando.
 *
 * ⚠ **Todo es un filtro.** Una tarjeta que dice «4 vencidas» y no deja verlas obliga a
 * buscarlas a mano en la tabla, que es justo el trabajo que la tarjeta venía a
 * ahorrar. Pulsar filtra; pulsar otra vez lo quita.
 *
 * ⚠ **Los montos van por moneda, uno debajo de otro.** Sumar bolívares con dólares
 * daría una cifra en una unidad que no existe. Por eso ninguna tarjeta tiene «total».
 *
 * ⚠ **Las cifras vienen del servidor, no de las filas de la tabla**: la tabla está
 * paginada, y contar lo que llegó a la pantalla diría 25 donde hay 44.
 */
export function TableroOrdenes({
  datos,
  estado,
  alerta,
  proveedorId,
  onEstado,
  onAlerta,
  onProveedor,
}: {
  datos: Indicadores
  estado: string
  alerta: Alerta | null
  proveedorId: number | null
  onEstado: (estado: string) => void
  onAlerta: (alerta: Alerta | null) => void
  onProveedor: (id: number | null) => void
}) {
  const e = datos.por_estado
  const mes = MESES[Number(datos.mes.slice(5, 7)) - 1] ?? ""
  const alternar = (valor: string) => onEstado(estado === valor ? "" : valor)

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <TarjetaKpi
          titulo="Por pagar"
          detalle="Aprobadas, esperando a tesorería"
          Icono={Wallet}
          color="var(--info)"
          cantidad={e.APROBADA.cantidad}
          montos={e.APROBADA.montos}
          activa={estado === "APROBADA"}
          onClick={() => alternar("APROBADA")}
        />
        <TarjetaKpi
          titulo="En aprobación"
          detalle="Esperando la segunda firma"
          Icono={Hourglass}
          color="var(--warning)"
          cantidad={e.EN_APROBACION.cantidad}
          montos={e.EN_APROBACION.montos}
          activa={estado === "EN_APROBACION"}
          onClick={() => alternar("EN_APROBACION")}
        />
        <TarjetaKpi
          titulo="En borrador"
          detalle="Se están redactando"
          Icono={PenLine}
          color="var(--fg-muted)"
          cantidad={e.BORRADOR.cantidad}
          montos={e.BORRADOR.montos}
          activa={estado === "BORRADOR"}
          onClick={() => alternar("BORRADOR")}
        />
        <TarjetaKpi
          titulo={`Pagado en ${mes}`}
          detalle="Por la fecha valor del pago"
          Icono={CheckCircle2}
          color="var(--success)"
          cantidad={datos.pagado_mes.reduce((s, p) => s + p.cantidad, 0)}
          montos={datos.pagado_mes}
          activa={estado === "EJECUTADA"}
          onClick={() => alternar("EJECUTADA")}
        />
      </div>

      <div className="grid gap-3 lg:grid-cols-[2fr_1fr]">
        <Circuito datos={datos} estado={estado} onEstado={alternar} />
        <div className="tarjeta space-y-2 p-3">
          <h2 className="text-sm font-semibold">Lo que se está atascando</h2>
          <div className="grid grid-cols-2 gap-2">
            <ChipAlerta
              Icono={AlarmClock}
              etiqueta="Vencidas"
              cantidad={datos.alertas.vencidas}
              color="var(--destructive)"
              activa={alerta === "vencidas"}
              onClick={() => onAlerta(alerta === "vencidas" ? null : "vencidas")}
            />
            <ChipAlerta
              Icono={Hourglass}
              etiqueta={`Vencen en ${datos.dias_por_vencer} días`}
              cantidad={datos.alertas.por_vencer}
              color="var(--warning)"
              activa={alerta === "por_vencer"}
              onClick={() => onAlerta(alerta === "por_vencer" ? null : "por_vencer")}
            />
            <ChipAlerta
              Icono={FileQuestion}
              etiqueta="Sin factura adjunta"
              cantidad={datos.alertas.sin_factura}
              color="var(--warning)"
              activa={alerta === "sin_factura"}
              onClick={() => onAlerta(alerta === "sin_factura" ? null : "sin_factura")}
            />
            <ChipAlerta
              Icono={Hash}
              etiqueta="Sin número de factura"
              cantidad={datos.alertas.sin_numero}
              color="var(--info)"
              activa={alerta === "sin_numero"}
              onClick={() => onAlerta(alerta === "sin_numero" ? null : "sin_numero")}
            />
          </div>
        </div>
      </div>

      {datos.proveedores.length > 0 && (
        <div className="tarjeta p-3">
          <h2 className="mb-2 text-sm font-semibold">Proveedores con más órdenes pendientes</h2>
          <div className="flex flex-wrap gap-2">
            {datos.proveedores.map((p) => {
              const activo = proveedorId === p.proveedor_id
              return (
                <button
                  key={p.proveedor_id}
                  type="button"
                  aria-pressed={activo}
                  onClick={() => onProveedor(activo ? null : p.proveedor_id)}
                  className={`foco-cristal flex min-h-9 items-center gap-2 rounded-[var(--radius-control)] border px-3 py-1 text-sm ${activo ? "activo-cristal" : "toque-cristal"}`}
                  style={{ borderColor: "var(--cristal-filo)" }}
                >
                  <span className="max-w-[16rem] truncate">{p.proveedor}</span>
                  <span className="tabular-nums font-semibold">{p.cantidad}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Cuántas órdenes hay en cada tramo del circuito, como una sola barra.
 *
 * ⚠ **Por número de órdenes, no por monto**: el monto mezclaría monedas. Las anuladas
 * quedan fuera de la barra —no son un tramo, son la salida— y se dicen aparte.
 */
function Circuito({
  datos,
  estado,
  onEstado,
}: {
  datos: Indicadores
  estado: string
  onEstado: (estado: string) => void
}) {
  const total = TRAMOS.reduce((s, t) => s + datos.por_estado[t.estado].cantidad, 0)
  const anuladas = datos.por_estado.ANULADA.cantidad

  return (
    <div className="tarjeta space-y-3 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">El circuito</h2>
        <span className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
          {total} {total === 1 ? "orden" : "órdenes"}
          {anuladas > 0 && ` · ${anuladas} anuladas`}
        </span>
      </div>

      {total === 0 ? (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Sin órdenes todavía.
        </p>
      ) : (
        <>
          {/* 2 px de hueco entre tramos, para que dos colores vecinos no se fundan. */}
          <div className="flex h-3 gap-[2px] overflow-hidden rounded-full" aria-hidden="true">
            {TRAMOS.map((t) => {
              const n = datos.por_estado[t.estado].cantidad
              if (n === 0) return null
              return (
                <div
                  key={t.estado}
                  title={`${t.etiqueta}: ${n} (${porcentaje(n, total)})`}
                  className="h-full first:rounded-l-full last:rounded-r-full"
                  style={{
                    width: `${(n / total) * 100}%`,
                    // Un tramo de una orden entre cien seguiría existiendo: se ve.
                    minWidth: 4,
                    backgroundColor: t.color,
                    opacity: estado === "" || estado === t.estado ? 1 : 0.35,
                  }}
                />
              )
            })}
          </div>
          {/* La leyenda ES el control: cada tramo con su nombre, su cifra y su parte.
              Sin ella la barra dependería sólo del color. */}
          <div className="grid grid-cols-2 gap-1 sm:grid-cols-4">
            {TRAMOS.map((t) => {
              const n = datos.por_estado[t.estado].cantidad
              const activo = estado === t.estado
              return (
                <button
                  key={t.estado}
                  type="button"
                  aria-pressed={activo}
                  onClick={() => onEstado(t.estado)}
                  className={`foco-cristal flex min-h-9 items-center gap-2 rounded-[var(--radius-control)] px-2 py-1 text-left text-xs ${activo ? "activo-cristal" : "toque-cristal"}`}
                >
                  <span
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: t.color }}
                    aria-hidden="true"
                  />
                  <span className="min-w-0">
                    <span className="block truncate">{t.etiqueta}</span>
                    <span className="tabular-nums" style={{ color: "var(--fg-muted)" }}>
                      {n} · {porcentaje(n, total)}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

function ChipAlerta({
  Icono,
  etiqueta,
  cantidad,
  color,
  activa,
  onClick,
}: {
  Icono: typeof FileWarning
  etiqueta: ReactNode
  cantidad: number
  color: string
  activa: boolean
  onClick: () => void
}) {
  // En cero no se esconde: «0 vencidas» es una buena noticia que también se lee.
  const nada = cantidad === 0
  return (
    <button
      type="button"
      aria-pressed={activa}
      onClick={onClick}
      disabled={nada && !activa}
      className={`foco-cristal flex min-h-11 items-center gap-2 rounded-[var(--radius-control)] border px-2 py-1 text-left text-xs disabled:opacity-60 ${activa ? "activo-cristal" : "toque-cristal"}`}
      style={{ borderColor: "var(--cristal-filo)" }}
    >
      <Icono
        size={16}
        strokeWidth={1.5}
        className="shrink-0"
        style={{ color: nada ? "var(--fg-muted)" : color }}
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1 leading-tight">{etiqueta}</span>
      <span className="text-base font-semibold tabular-nums">{cantidad}</span>
    </button>
  )
}

function porcentaje(parte: number, total: number): string {
  return total === 0 ? "0 %" : `${Math.round((parte / total) * 100)} %`
}
